import { Box, Divider, Typography } from '@mui/material';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export interface StampedDocument {
  document: string;
  lastStampingYear: string;
}

export interface EconomicActivity {
  code?: string;
  description?: string;
  [key: string]: string | undefined;
}

export interface TaxSituationData {
  name: string;
  identifier: string;
  consultationDate: string;
  hasActivityStart: boolean;
  activityStartDate: string | null;
  authorizedForeignCurrency: boolean;
  smallBusiness: boolean;
  economicActivities: EconomicActivity[];
  stampedDocuments: StampedDocument[];
}

export interface TaxSituationResponse {
  message: string;
  result: TaxSituationData;
}

const formatBoolean = (value: boolean) => (value ? 'Sí' : 'No');

const TaxSituationResponse = (props: {
  response: { data?: TaxSituationResponse };
}) => {
  const dispatch = useDispatch();
  const { response } = props;
  const [validResponse, setValidResponse] = useState(true);
  const [data, setData] = useState<TaxSituationData | null>(null);

  const verifyResponse = () => {
    const raw = response?.data;
    const result = raw?.result;

    if (!result) {
      showAlert(
        dispatch,
        'error',
        'No se pudieron obtener los datos de la consulta. Intenta más tarde.',
      );
      setValidResponse(false);
      return false;
    }

    setData(result);
    setValidResponse(true);
    return true;
  };

  useEffect(() => {
    verifyResponse();
  }, [response]);

  if (!validResponse || !data) {
    return null;
  }

  const economicActivitiesHeaders = Object.keys(
    data.economicActivities?.[0] || { code: '', description: '' },
  ).map((key) => ({
    key,
    label:
      key === 'code' ? 'Código' : key === 'description' ? 'Descripción' : key,
  }));

  const stampedDocumentsHeaders = [
    { key: 'document', label: 'Documento' },
    { key: 'lastStampingYear', label: 'Último año timbraje' },
  ];

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: 2 }}>
        Datos del contribuyente
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 1 }}>
        <strong>Nombre o Razón Social:</strong> {data.name}
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 1 }}>
        <strong>RUT:</strong> {data.identifier}
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 1 }}>
        <strong>Fecha de consulta:</strong> {data.consultationDate}
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 1 }}>
        <strong>Inicio de actividades:</strong>{' '}
        {formatBoolean(data.hasActivityStart)}
        {data.activityStartDate && ` (${data.activityStartDate})`}
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 1 }}>
        <strong>Moneda extranjera autorizada:</strong>{' '}
        {formatBoolean(data.authorizedForeignCurrency)}
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: 1 }}>
        <strong>Pequeño contribuyente:</strong>{' '}
        {formatBoolean(data.smallBusiness)}
      </Typography>

      {Array.isArray(data.economicActivities) &&
        data.economicActivities.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, marginBottom: 2 }}
            >
              Actividades económicas
            </Typography>
            <EnhancedTable
              dataHeaders={[
                { key: 'activity', label: 'Actividad' },
                { key: 'code', label: 'Código' },
                { key: 'category', label: 'Categoría' },
                { key: 'affectsVAT', label: 'Sujeta a IVA' },
                { key: 'date', label: 'Fecha de inicio' },
              ]}
              data={data.economicActivities}
              rowsPerPageDefault={10}
            />
          </>
        )}

      {Array.isArray(data.stampedDocuments) &&
        data.stampedDocuments.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, marginBottom: 2 }}
            >
              Documentos timbrados
            </Typography>
            <EnhancedTable
              dataHeaders={stampedDocumentsHeaders}
              data={data.stampedDocuments}
              rowsPerPageDefault={10}
            />
          </>
        )}
    </Box>
  );
};

export default TaxSituationResponse;
